// Lib
import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * Returns the browser-computed background colour for a referenced element.
 *
 * This is useful when an element's visible colour is derived from CSS variables
 * or inherited styles rather than an explicit value in React props.
 *
 * Pass `customProperty` to resolve a background colour held in a custom property instead of the
 * element's own background, for elements that paint from a token or through a pseudo element.
 */
const useComputedBackgroundColor = <T extends HTMLElement>(
    ref: RefObject<T> | null,
    customProperty?: string,
): string | null => {
    const [computedColor, setComputedColor] = useState<string | null>(null);

    useLayoutEffect(() => {
        const element = ref?.current;
        if (!element) {
            setComputedColor(null);
            return;
        }

        const styles = getComputedStyle(element);
        const backgroundColor = customProperty
            ? styles.getPropertyValue(customProperty).trim()
            : styles.backgroundColor;

        if (!backgroundColor || backgroundColor === 'rgba(0, 0, 0, 0)' || backgroundColor === 'transparent') {
            setComputedColor(null);
            return;
        }

        setComputedColor(backgroundColor);
    });

    return computedColor;
};

export default useComputedBackgroundColor;
